import { ReactNode } from 'react'
import {
  ArrowBack,
  Calculate,
  CheckCircle,
  Close,
  CloudDone,
  Draw,
  HourglassTop,
  Person,
  UploadFile,
} from '@mui/icons-material'
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import { ProductHeaderActions } from '@/components/product-switcher/product-header-actions'
import { HeaderPortal } from '@/components/layouts/app-layout/header-slot'
import { OCTA_DESK_PHONE } from '@/constants/phones'
import { journeyStages } from '@/pages/credit/journeys/allowance/allowance-flow'

const stageIcons = [Calculate, Person, UploadFile, HourglassTop, Draw]

export function JourneyShell({
  productName = 'Mesada',
  stage,
  progress,
  title,
  hint,
  footerNote,
  onBack,
  onExit,
  onNext,
  nextLabel,
  nextDisabled,
  saving,
  showSaved,
  done,
  children,
}: {
  productName?: string
  stage: number
  progress: { current: number; total: number }
  title: string
  hint?: string
  footerNote?: ReactNode
  onBack?: () => void
  onExit: () => void
  onNext?: () => void
  nextLabel: string
  nextDisabled?: boolean
  saving?: 'saving' | 'saved' | 'error'
  showSaved?: boolean
  done?: boolean
  children: ReactNode
}) {
  const desktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up('md'))
  const stageName = journeyStages[stage] ?? journeyStages[0]
  const saveLabel =
    saving === 'saving'
      ? 'Salvando…'
      : saving === 'error'
        ? 'Não salvou. Toque para tentar de novo.'
        : 'Salvo automaticamente'

  const saved = showSaved ? (
    <Typography
      component="button"
      type="button"
      onClick={saving === 'error' ? onNext : undefined}
      variant="caption"
      color="text.secondary"
      sx={{
        border: 0,
        background: 'transparent',
        display: 'inline-flex',
        gap: 0.5,
        alignItems: 'center',
        justifyContent: 'center',
        cursor: saving === 'error' ? 'pointer' : 'default',
        fontSize: 14,
        lineHeight: 1.2,
        p: 0,
      }}
    >
      <CloudDone sx={{ fontSize: 14, color: 'success.main' }} />
      {saveLabel}
    </Typography>
  ) : null

  const cta = (
    <Button
      variant="contained"
      onClick={onNext}
      disabled={nextDisabled || saving === 'saving'}
      sx={{ minHeight: 52, borderRadius: 1.5, fontSize: 15 }}
    >
      {nextLabel}
    </Button>
  )

  const header = !desktop ? (
    <Box sx={{ pb: 1.25 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: onBack ? '44px minmax(0, 1fr) 44px' : 'minmax(0, 1fr) 44px',
          alignItems: 'center',
          columnGap: 3,
          px: 1.5,
          pt: 2,
          pb: 1,
        }}
      >
        {onBack && (
          <IconButton aria-label="Voltar" color="primary" onClick={onBack}>
            <ArrowBack />
          </IconButton>
        )}
        <Typography fontWeight={500} sx={{ pl: 0.5, pr: 2 }}>
          {productName}
        </Typography>
        <IconButton aria-label="Sair" color="primary" onClick={onExit}>
          <Close />
        </IconButton>
      </Box>
      <ProgressBars stage={stage} progress={progress} done={done} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: 1.5,
          pt: 0.75,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {done ? (
            'Concluído'
          ) : (
            <>
              Etapa {stage + 1} de 5 ·{' '}
              <Box component="b" sx={{ color: 'text.primary', fontWeight: 500 }}>
                {stageName}
              </Box>
            </>
          )}
        </Typography>
      </Box>
    </Box>
  ) : (
    <Box sx={{ pt: 2, pb: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '40px minmax(0, 1fr) auto',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <IconButton aria-label="Sair da solicitação" color="primary" onClick={onExit}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight={500} sx={{ pl: 1 }}>
          {productName}
        </Typography>
        <ProductHeaderActions />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: 1,
        }}
      >
        {journeyStages.map((label, index) => {
          const Icon = done || index < stage ? CheckCircle : stageIcons[index]
          const state = done || index < stage ? 'done' : index === stage ? 'now' : 'next'
          return (
            <Box
              key={label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                color: state === 'next' ? 'text.disabled' : 'text.primary',
              }}
            >
              <StageBar width={stageWidth(index, stage, progress, done)} />
              <Typography variant="body2" fontWeight={500} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Icon sx={{ fontSize: 18 }} />
                {label}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )

  if (!desktop) {
    return (
      <>
        <HeaderPortal>{header}</HeaderPortal>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, pt: 0.5, display: 'grid', gap: 2, alignContent: 'start', pb: 16 }}>
          {title && (
            <Typography sx={{ fontSize: 22, fontWeight: 500, lineHeight: 1.25 }}>
              {title}
            </Typography>
          )}
          {hint && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
              {hint}
            </Typography>
          )}
          {children}
        </Box>

        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            p: 2,
            pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            display: 'grid',
            gap: 1.25,
          }}
        >
          {footerNote}
          {cta}
          {saved}
        </Box>
        </Box>
      </>
    )
  }

  return (
    <>
      <HeaderPortal>{header}</HeaderPortal>
      <Box sx={{ pb: 2 }}>
      <Box
        sx={{
          width: '100%',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: 3.5,
          display: 'grid',
          gap: 2,
        }}
      >
        {title && (
          <Typography sx={{ fontSize: 24, fontWeight: 500, lineHeight: 1.25 }}>
            {title}
          </Typography>
        )}
        {hint && (
          <Typography color="text.secondary" sx={{ mt: -1 }}>
            {hint}
          </Typography>
        )}
        {children}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            pt: 2,
          }}
        >
          <Button
            variant="text"
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{ visibility: onBack ? 'visible' : 'hidden' }}
          >
            Voltar
          </Button>
          {saved ?? <span />}
          {cta}
        </Box>
      </Box>

      <Typography color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
        Dúvidas no preenchimento?{' '}
        <Box
          component="a"
          href={`https://api.whatsapp.com/send?phone=${OCTA_DESK_PHONE}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'primary.main' }}
        >
          Fale com a gente
        </Box>
      </Typography>
      {footerNote && (
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
          {footerNote}
        </Typography>
      )}
    </Box>
    </>
  )
}

function stageWidth(
  index: number,
  stage: number,
  progress: { current: number; total: number },
  done?: boolean,
) {
  if (done || index < stage) return 100
  if (index === stage) return (progress.current / Math.max(progress.total, 1)) * 100
  return 0
}

function StageBar({ width }: { width: number }) {
  return (
    <Box sx={{ height: 4, borderRadius: '2px', bgcolor: 'action.hover', overflow: 'hidden' }}>
      <Box sx={{ width: `${width}%`, height: '100%', bgcolor: 'primary.light', transition: 'width 200ms' }} />
    </Box>
  )
}

function ProgressBars({
  stage,
  progress,
  done,
}: {
  stage: number
  progress: { current: number; total: number }
  done?: boolean
}) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '4px', px: 1.5 }}>
      {journeyStages.map((label, index) => (
        <StageBar key={label} width={stageWidth(index, stage, progress, done)} />
      ))}
    </Box>
  )
}
